import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import {
  forceCollide,
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type Simulation,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from 'd3-force'
import { ArrowUp, Check, ExternalLink, GitMerge, Hourglass, Maximize2, X } from 'lucide-react'
import { api, mutate } from '@/runtime'
import {
  useAllMirrors,
  useAllPages,
  useAllProjects,
  useAllRuns,
  useAllTasks,
  useNames,
  useProjectOf,
  useSessionCards,
} from '@/db/hooks'
import type { AgentStats, Builds, ClusterSummary } from '@/db/api'
import { useNow } from '@/hooks/useNow'
import { useStream } from '@/state/stream'
import {
  agentGraph,
  declutterLabels,
  effectiveLayers,
  fitList,
  fitView,
  headingsFor,
  forMode,
  layerInMode,
  layerRequired,
  layoutMode,
  leadOf,
  MODES,
  MAX_ZOOM,
  MIN_ZOOM,
  neighbours,
  pinchView,
  screenSize,
  type AgentGraph,
  type GraphLink,
  type Touch2,
  type GraphMode,
  type GraphNode,
  type Layer,
  type Layers,
  type Pt,
} from '@/lib/agentGraph'
import { PRIME_PATH } from '@/components/PrimeMark'
import { graphKindColour, graphPhaseColour, hueFor, PRIME_MARK, tokens } from '@/lib/colour'
import { fmtAgo } from '@/lib/time'
import { podWords } from '@/lib/podWords'
import { AppTags, HostDetail, ProblemDetail } from '@/components/ClusterBits'
import { runsVersion, shipping } from '@/lib/pipeline'
import { stepOf } from '@/kinds/agentrun/toolSteps'
import { isOver } from '@/kinds/agentrun/schema'
import type { AgentRunResource } from '@/kinds/agentrun/apply'
import type { GitIssueResource } from '@/kinds/git/apply'
import type { SuggestionResource } from '@/kinds/suggestion'
import { MirrorSheet } from '@/kinds/git/components'
import { repoName } from '@/kinds/agentrun/components'
import { splitArtifactNote } from '@/kinds/agentrun/artifactNote'
import { appLineOf } from '@/kinds/agentrun/news'
import { RunTag, useRunStatus } from '@/components/RunStatus'
import { ShipPipeline } from '@/components/ShipPipeline'
import { IconButton, PageTitle, Sheet, Tag, cx } from '@/components/ui'
import { GraphLayersSheet, GraphPhoneBar, WaitingDot } from '@/components/GraphControls'
import { WaitingBoard, WaitingDock } from '@/components/Waiting'
import { useFactoryEnabled } from '@/kinds/agentrun/components'
import { moving, waitingOf, type Holder, type Waiting } from '@/lib/waiting'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { acceptCard, answerAsk, Proposal, useCardTap } from '@/views/Chat'
import { dismissSuggestion } from '@/lib/suggestions'

// ---- the graph (/agents/graph) ---------------------------------------------
//
// The factory at work: every coding agent as a node around Prime, linked to the
// task it works for and that task's project, and around them the issues in play,
// the pages being used, the builds and a ship on their way to staging and prod,
// and Prime's cards waiting for the owner (answered from here, through the same
// card as the thread). Laid out by a force simulation. Made for a laptop window
// kept open beside other work (and, below md, a phone: see `phone` below): it follows the factory live, hover lights a
// node's neighbours, a click opens the node beside the graph. Wheel zooms,
// dragging the background pans, dragging a node moves it.
//
// The hub of DevOS (2026-10-10, "Graph is the hub"): beside it, docked on a
// laptop and in a sheet on a phone, the waiting board (components/Waiting.tsx),
// so what is on the owner is answered without leaving it. It took over the old
// Overview, Board, Reviews, Issues and Infrastructure pages (their paths
// redirect here; ?mode= opens a view).
//
// Layers (chips, per device) choose what is in it, among them the cluster's nodes
// with their load, the sandboxes on them, pods and Argo apps that need a look, and
// today's spend. Modes (a switch, per device) choose how it is arranged: Everything
// floats; Work, Pipeline, Infra and Approvals are laid out by `layoutMode` (pure)
// and the nodes glide there.

type SimNode = GraphNode & SimulationNodeDatum & { r: number }
type SimLink = SimulationLinkDatum<SimNode> & { kind: GraphLink['kind'] }
type View = { k: number; x: number; y: number }

const RADIUS: Record<GraphNode['kind'], number> = {
  prime: 22,
  agent: 13,
  task: 7,
  project: 10,
  issue: 7,
  doc: 7,
  build: 10,
  ship: 12,
  env: 9,
  card: 10,
  node: 14,
  sandbox: 6,
  problem: 8,
  app: 8,
  spend: 11,
}
const DISTANCE: Record<GraphLink['kind'], number> = {
  'looks-after': 120,
  'works-on': 64,
  'part-of': 48,
  tracks: 42,
  uses: 46,
  builds: 80,
  deploys: 64,
  asks: 50,
  'runs-on': 44,
  spends: 52,
}
/** kinds too many to label at a glance: named when zoomed in, lit or chosen */
const SMALL = new Set<GraphNode['kind']>(['task', 'issue', 'doc', 'sandbox'])
/**
 * The pipeline keeps a lane down the left, read top to bottom: builds, staging,
 * prod, then a ship on its way; everything else gathers round Prime.
 */
const PIPELINE = new Set<GraphNode['kind']>(['build', 'env', 'ship'])
/** the cluster keeps a lane down the right */
const INFRA = new Set<GraphNode['kind']>(['node', 'sandbox', 'problem', 'app'])
const laneX = (d: GraphNode) =>
  d.kind === 'env' || d.kind === 'ship' ? -340 : d.kind === 'build' ? -260 : INFRA.has(d.kind) ? 340 : 0
const laneY = (d: GraphNode) =>
  d.kind === 'build'
    ? -140
    : d.kind === 'env'
      ? d.env?.name === 'prod'
        ? 90
        : -20
      : d.kind === 'ship'
        ? 200
        : d.kind === 'node'
          ? -40
          : d.kind === 'app'
            ? 120
            : 0

/** room round the framed graph: in graph units on a desk, in pixels on a phone (its labels are a fixed size there) */
const fitPad = (phone: boolean) => (phone ? { x: 84, y: 44 } : { x: 100, y: 60 })

/**
 * The view that frames a mode's layout. Approvals is a list, read from its top
 * left with room for the ✓ and × before each card and its title after (on a phone
 * just the cards: what they concern is a pan to the right); the rest fits whole.
 */
const frameFor = (
  g: AgentGraph,
  mode: GraphMode,
  at: Record<string, Pt>,
  size: { w: number; h: number },
  phone: boolean,
) => {
  const heads = headingsFor(g, mode, at)
  if (mode === 'approvals') {
    const cards = g.nodes.filter((n) => n.kind === 'card').flatMap((n) => (at[n.id] ? [at[n.id]!] : []))
    const all = g.nodes.flatMap((n) => (at[n.id] ? [at[n.id]!] : []))
    const titles = cards.map((p) => ({ x: p.x + (phone ? 250 : 330), y: p.y }))
    return fitList(
      [...(phone ? cards : all), ...titles, ...heads],
      size,
      { left: phone ? 108 : 76, right: phone ? 12 : 60, top: 36 },
      phone ? 1 : 1.1,
    )
  }
  return fitView(
    [...g.nodes.flatMap((n) => (at[n.id] ? [at[n.id]!] : [])), ...heads],
    size,
    fitPad(phone),
    phone,
  )
}

const weekKey = 'graph-week'
const readWeek = () => {
  try {
    return localStorage.getItem(weekKey) === '1'
  } catch {
    return false
  }
}

// the factory's layers, each shown or hidden per device. Waiting cards are not one of
// them: they always show, and the Approvals mode and the waiting panel are their views,
// so a third control for the same idea (a chip) would only be one more way to lose them
const LAYERS: { key: Layer; label: string }[] = [
  { key: 'issues', label: 'Issues' },
  { key: 'docs', label: 'Pages' },
  { key: 'pipelines', label: 'Pipelines' },
  { key: 'envs', label: 'Prod and staging' },
  { key: 'nodes', label: 'Nodes' },
  { key: 'sandboxes', label: 'Sandboxes' },
  { key: 'health', label: 'Pods and apps' },
  { key: 'spend', label: 'Spend' },
]
// the infrastructure and spend layers are off until asked for (Infra mode brings its own)
const ALL_LAYERS: Layers = {
  issues: true,
  docs: true,
  pipelines: true,
  approvals: true,
  envs: true,
  nodes: false,
  sandboxes: false,
  health: false,
  spend: false,
}
const layersKey = 'graph-layers'
const readLayers = (): Layers => {
  try {
    const saved = JSON.parse(localStorage.getItem(layersKey) ?? '{}') as Partial<Layers>
    return { ...ALL_LAYERS, ...saved, approvals: true }
  } catch {
    return ALL_LAYERS
  }
}

/** builds in flight or a ship on its way are asked after every few seconds, a quiet factory every 15 */
const buildsEvery = (b: Builds | null) =>
  b && (b.builds.some((x) => x.state === 'queued' || x.state === 'building') || shipping(b)) ? 4_000 : 15_000

// the waiting board docked beside the graph (not a phone), per device; on unless closed
const waitingKey = 'graph-waiting'
const readWaiting = () => {
  try {
    return localStorage.getItem(waitingKey) !== '0'
  } catch {
    return true
  }
}

const modeKey = 'graph-mode'
const readMode = (): GraphMode => {
  try {
    const m = localStorage.getItem(modeKey)
    return MODES.some((x) => x.key === m) ? (m as GraphMode) : 'everything'
  } catch {
    return 'everything'
  }
}

// each read has a function of its own, so the poll below has a stable one to hold
const loadBuilds = () => api.builds()
const loadCluster = () => api.cluster()
const loadStats = () => api.agentStats(1)

/**
 * A read repeated while `on` and the page is visible, every `ms` (a function of
 * the last answer, so builds in flight are asked after faster). A failed read
 * (offline, no factory) is simply nothing to draw.
 */
const usePolled = <T,>(
  on: boolean,
  load: () => Promise<T>,
  ms: number | ((last: T | null) => number),
): T | null => {
  const [data, setData] = useState<T | null>(null)
  const wait = useRef(ms)
  useEffect(() => {
    wait.current = ms
  })
  useEffect(() => {
    if (!on) return
    let stale = false
    let timer = 0
    // each poll() supersedes the chain before it, so a visibility change while a read is in
    // flight never leaves two timers going
    let gen = 0
    // one failed read (prod rolling mid-ship) keeps the last answer; two in a row clear it
    let failed = 0
    let last: T | null = null
    const poll = () => {
      window.clearTimeout(timer)
      const g = ++gen
      const next = () => {
        if (stale || g !== gen) return
        const m = wait.current
        timer = window.setTimeout(poll, typeof m === 'function' ? m(last) : m)
      }
      if (document.visibilityState !== 'visible') return next()
      load()
        .then((b) => {
          if (stale || g !== gen) return
          failed = 0
          last = b
          setData(b)
          next()
        })
        .catch(() => {
          if (stale || g !== gen) return
          if (++failed >= 2) {
            last = null
            setData(null)
          }
          next()
        })
    }
    poll()
    document.addEventListener('visibilitychange', poll)
    return () => {
      stale = true
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', poll)
    }
  }, [on, load])
  return on ? data : null
}

/** The phase to draw: the streamed one when it is newer than the mirror (useRunPhase, for many). */
const usePhaseOf = () => {
  const live = useStream((s) => s.runPhase)
  return useMemo(
    () => (r: AgentRunResource) => {
      const l = r.spec.name ? live[r.spec.name] : undefined
      if (!l || isOver(r.spec.status)) return r.spec.status
      return Date.parse(r.spec.updated_at || '0') > l.at ? r.spec.status : l.phase
    },
    [live],
  )
}

/**
 * The colour an agent is drawn in: who holds its work (lib/waiting.ts), as every
 * chip says it: the owner's turn warm, Prime's quiet, running in the accent; an
 * agent nobody holds by its phase, a finished turn as idle.
 */
const holderPhase = (holder: Holder | undefined, phase: string | undefined) =>
  holder === 'you'
    ? phase === 'Failed'
      ? 'Failed'
      : 'Waiting'
    : holder === 'prime'
      ? 'Suspended'
      : holder && moving(holder)
        ? phase === 'Pending'
          ? 'Pending'
          : 'Working'
        : phase === 'Waiting'
          ? 'Suspended'
          : phase
const nodeColour = (n: GraphNode, holder?: Holder) => {
  if (n.kind === 'agent') return graphPhaseColour(holderPhase(holder, n.phase))
  if (n.kind === 'project') return tokens(n.project?.spec.colour ?? hueFor(`project:${n.id}`)).stripe
  if (n.kind === 'task') return n.phase === 'done' ? 'var(--color-faint)' : 'var(--color-fg-2)'
  if (n.kind === 'prime') return PRIME_MARK.color
  return graphKindColour(n.kind, n.phase)
}

/** A node's shape at the origin: the same drawing for the graph and its legend. */
const Shape = ({
  n,
  r,
  colour,
}: {
  n: Pick<GraphNode, 'kind' | 'phase' | 'host'>
  r: number
  colour: string
}) => {
  switch (n.kind) {
    case 'prime':
      return (
        <>
          <circle r={r} fill={PRIME_MARK.disc} stroke={PRIME_MARK.color} strokeWidth={1.5} />
          <g
            transform={`translate(${-r * 0.5} ${-r * 0.5}) scale(${(r * 1.0) / 24})`}
            color={PRIME_MARK.color}
          >
            <path d={PRIME_PATH} fill="currentColor" />
            <path d="M4 21H20" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" />
          </g>
        </>
      )
    case 'task':
      return (
        <rect
          x={-r}
          y={-r}
          width={r * 2}
          height={r * 2}
          rx={2.5}
          fill="var(--color-panel)"
          stroke={colour}
          strokeWidth={1.5}
        />
      )
    case 'project':
      return <circle r={r} fill="var(--color-panel)" stroke={colour} strokeWidth={2.5} />
    case 'issue':
      // a diamond
      return (
        <path
          d={`M0,${-r * 1.25}L${r * 1.25},0L0,${r * 1.25}L${-r * 1.25},0Z`}
          fill="var(--color-panel)"
          stroke={colour}
          strokeWidth={1.6}
          strokeLinejoin="round"
        />
      )
    case 'doc': {
      // a page with its corner folded
      const w = r * 0.85
      const h = r * 1.1
      const f = r * 0.5
      return (
        <>
          <path
            d={`M${-w},${-h}H${w - f}L${w},${-h + f}V${h}H${-w}Z`}
            fill="var(--color-panel)"
            stroke={colour}
            strokeWidth={1.5}
            strokeLinejoin="round"
          />
          <path d={`M${w - f},${-h}V${-h + f}H${w}`} fill="none" stroke={colour} strokeWidth={1.2} />
        </>
      )
    }
    case 'build': {
      // a hexagon, a step of the pipeline
      const pts = Array.from({ length: 6 }, (_, i) => {
        const a = (Math.PI / 3) * i
        return `${(Math.cos(a) * r).toFixed(2)},${(Math.sin(a) * r).toFixed(2)}`
      }).join(' ')
      return (
        <polygon
          points={pts}
          fill={n.phase === 'building' ? colour : 'var(--color-panel)'}
          fillOpacity={n.phase === 'building' ? 0.25 : 1}
          stroke={colour}
          strokeWidth={1.8}
          strokeDasharray={n.phase === 'queued' ? '3 2.5' : undefined}
          strokeLinejoin="round"
        />
      )
    }
    case 'ship':
      // an arrow on its way
      return (
        <path
          d={`M${-r},${-r * 0.75}H${r * 0.2}L${r},0L${r * 0.2},${r * 0.75}H${-r}L${-r * 0.55},0Z`}
          fill={colour}
          fillOpacity={0.3}
          stroke={colour}
          strokeWidth={1.8}
          strokeLinejoin="round"
        />
      )
    case 'env':
      // a server rack: a short rounded bar
      return (
        <>
          <rect
            x={-r * 1.3}
            y={-r * 0.7}
            width={r * 2.6}
            height={r * 1.4}
            rx={r * 0.35}
            fill="var(--color-panel)"
            stroke={colour}
            strokeWidth={1.5}
          />
          <circle cx={-r * 0.75} r={r * 0.2} fill={colour} />
        </>
      )
    case 'card':
      // a speech bubble: Prime asking
      return (
        <>
          <path
            d={`M${-r},${-r * 0.8}a${r * 0.3},${r * 0.3} 0 0 1 ${r * 0.3},${-r * 0.3}H${r * 0.7}a${r * 0.3},${r * 0.3} 0 0 1 ${r * 0.3},${r * 0.3}V${r * 0.4}a${r * 0.3},${r * 0.3} 0 0 1 ${-r * 0.3},${r * 0.3}H${-r * 0.1}L${-r * 0.6},${r * 1.15}V${r * 0.7}H${-r * 0.7}a${r * 0.3},${r * 0.3} 0 0 1 ${-r * 0.3},${-r * 0.3}Z`}
            fill={colour}
            stroke="var(--color-ink)"
            strokeWidth={1.2}
            strokeLinejoin="round"
          />
          <text textAnchor="middle" y={r * 0.33} fontSize={r * 1.05} fontWeight={700} fill="var(--color-ink)">
            ?
          </text>
        </>
      )
    case 'node': {
      // a server: a rack with its load as a bar, and a roof when it is home
      const cpu = n.host && n.host.cpu >= 0 ? Math.min(1, n.host.cpu) : 0
      return (
        <>
          <rect
            x={-r * 1.25}
            y={-r * 0.8}
            width={r * 2.5}
            height={r * 1.6}
            rx={r * 0.25}
            fill="var(--color-panel)"
            stroke={colour}
            strokeWidth={1.6}
          />
          <rect
            x={-r * 0.95}
            y={-r * 0.12}
            width={r * 1.9 * cpu}
            height={r * 0.24}
            rx={r * 0.12}
            fill={colour}
            fillOpacity={0.85}
          />
          <rect
            x={-r * 0.95}
            y={-r * 0.12}
            width={r * 1.9}
            height={r * 0.24}
            rx={r * 0.12}
            fill="none"
            stroke={colour}
            strokeOpacity={0.4}
            strokeWidth={0.8}
          />
          {n.host?.home && (
            <path
              d={`M${-r * 0.7},${-r * 0.8}L0,${-r * 1.45}L${r * 0.7},${-r * 0.8}`}
              fill="none"
              stroke={colour}
              strokeWidth={1.6}
              strokeLinejoin="round"
              strokeLinecap="round"
            />
          )}
        </>
      )
    }
    case 'sandbox':
      // a small box, dashed until it is up
      return (
        <rect
          x={-r}
          y={-r}
          width={r * 2}
          height={r * 2}
          rx={r * 0.4}
          fill="var(--color-panel)"
          stroke={colour}
          strokeWidth={1.4}
          strokeDasharray={n.phase === 'ready' ? undefined : '2.5 2'}
        />
      )
    case 'problem':
      // a triangle: look at this
      return (
        <>
          <path
            d={`M0,${-r * 1.15}L${r * 1.15},${r * 0.9}H${-r * 1.15}Z`}
            fill="var(--color-panel)"
            stroke={colour}
            strokeWidth={1.7}
            strokeLinejoin="round"
          />
          <path d={`M0,${-r * 0.3}V${r * 0.3}`} stroke={colour} strokeWidth={1.6} strokeLinecap="round" />
        </>
      )
    case 'app':
      // Argo's app: a ring with a dot, in its health's colour
      return (
        <>
          <circle r={r} fill="var(--color-panel)" stroke={colour} strokeWidth={1.8} />
          <circle r={r * 0.38} fill={colour} />
        </>
      )
    case 'spend':
      // a coin
      return (
        <>
          <circle r={r} fill="var(--color-panel)" stroke={colour} strokeWidth={1.6} />
          <text textAnchor="middle" dy="0.36em" fontSize={r * 1.05} fontWeight={600} fill={colour}>
            $
          </text>
        </>
      )
  }
  // an agent
  return (
    <circle
      r={r}
      fill={colour}
      fillOpacity={n.phase === 'Ended' || n.phase === 'Suspended' ? 0.45 : 1}
      stroke="var(--color-ink)"
      strokeWidth={2}
    />
  )
}

/** a label's size, and the words it carries (long ones are cut) */
const labelSize = (n: GraphNode) =>
  SMALL.has(n.kind) || n.kind === 'project' || n.kind === 'env' || n.kind === 'node' ? 10.5 : 12
const labelText = (n: GraphNode, listed = false, phone = false) => {
  // a card in Approvals' list has its row to itself (a phone's row is narrower)
  const max =
    listed && n.kind === 'card'
      ? phone
        ? 32
        : 46
      : n.kind === 'task' || n.kind === 'issue' || n.kind === 'doc'
        ? 26
        : 34
  return n.label.length > max ? n.label.slice(0, max - 1) + '…' : n.label
}
/** when two labels collide the higher one stays: what is waiting on you, then the rest */
const LABEL_PRIORITY: Record<GraphNode['kind'], number> = {
  prime: 90,
  card: 80,
  agent: 70,
  ship: 62,
  env: 60,
  node: 60,
  project: 55,
  build: 50,
  problem: 50,
  app: 48,
  spend: 45,
  task: 30,
  issue: 20,
  doc: 20,
  sandbox: 15,
}

export const AgentGraphView = () => {
  const runs = useAllRuns()
  const tasks = useAllTasks()
  const projects = useAllProjects()
  const projectOf = useProjectOf()
  const now = useNow(60_000)
  const phaseOf = usePhaseOf()
  // below md the page is a phone's: controls under the canvas, a node's detail in a sheet, pinch to zoom
  const phone = useMediaQuery('(max-width: 767px)')
  const [layersOpen, setLayersOpen] = useState(false)
  const [week, setWeek] = useState(readWeek)
  const setWeekSaved = (on: boolean) => {
    setWeek(on)
    try {
      localStorage.setItem(weekKey, on ? '1' : '0')
    } catch {
      // per device, best effort
    }
  }
  const [layers, setLayers] = useState(readLayers)
  // a link may name the view (the old Board and Infrastructure pages, an alert's push)
  const asked = (useSearch({ strict: false }) as { mode?: GraphMode }).mode
  const [mode, setModeState] = useState(() => asked ?? readMode())
  const [askedSeen, setAskedSeen] = useState(asked)
  if (asked !== askedSeen) {
    setAskedSeen(asked)
    if (asked) setModeState(asked)
  }
  const factory = useFactoryEnabled()
  const [docked, setDockedState] = useState(readWaiting)
  const setDocked = (v: boolean) => {
    setDockedState(v)
    try {
      localStorage.setItem(waitingKey, v ? '1' : '0')
    } catch {
      // per device, best effort
    }
  }
  const [waitingSheet, setWaitingSheet] = useState(false)
  const waitingShown = factory && (phone ? waitingSheet : docked)
  const setMode = (m: GraphMode) => {
    setModeState(m)
    try {
      localStorage.setItem(modeKey, m)
    } catch {
      // per device, best effort
    }
  }
  // a mode brings its own layers on; the chips decide the rest
  const on = useMemo(() => effectiveLayers(layers, mode), [layers, mode])
  const toggleLayer = (key: Layer) =>
    setLayers((l) => {
      const next = { ...l, [key]: !l[key] }
      try {
        localStorage.setItem(layersKey, JSON.stringify(next))
      } catch {
        // per device, best effort
      }
      return next
    })
  const issues = useAllMirrors('GitIssue') as GitIssueResource[] | undefined
  const pages = useAllPages()
  const lead = useMemo(() => leadOf(runs ?? [], phaseOf), [runs, phaseOf])
  const cards = useSessionCards(lead?.id ?? '')
  // the waiting board reads the same poll: builds, the ship and what waits for prod
  const builds = usePolled<Builds>(on.pipelines || on.envs || waitingShown, loadBuilds, buildsEvery)
  const cluster = usePolled<ClusterSummary>(on.nodes || on.sandboxes || on.health, loadCluster, 30_000)
  const stats = usePolled<AgentStats>(on.spend, loadStats, 60_000)
  // the whole graph the layers hold: the simulation keeps all of it, a mode shows part
  const graph = useMemo(
    () =>
      agentGraph({
        runs: runs ?? [],
        tasks: tasks ?? [],
        projects: projects ?? [],
        projectOf,
        now,
        week,
        phaseOf,
        issues: on.issues ? (issues ?? []) : undefined,
        pages: on.docs ? (pages ?? []) : undefined,
        builds: on.pipelines || on.envs ? builds : undefined,
        pipeline: on.pipelines,
        envs: on.envs,
        cards: on.approvals ? cards : undefined,
        infra:
          on.nodes || on.sandboxes || on.health
            ? { cluster, nodes: on.nodes, sandboxes: on.sandboxes, health: on.health }
            : undefined,
        stats: on.spend ? stats : undefined,
      }),
    [runs, tasks, projects, projectOf, now, week, phaseOf, on, issues, pages, builds, cards, cluster, stats],
  )
  const whole = useMemo(() => forMode(graph, mode), [graph, mode])
  // Approvals on a phone is the list of cards alone: what each concerns is in its panel (a tap),
  // not a column cut off at the glass's edge
  const shown = useMemo(
    () =>
      phone && mode === 'approvals'
        ? { nodes: whole.nodes.filter((n) => n.kind === 'card'), links: [] }
        : whole,
    [whole, phone, mode],
  )
  const targets = useMemo(() => layoutMode(shown, mode), [shown, mode])
  const targetKey = targets
    ? Object.entries(targets)
        .map(([id, p]) => `${id}:${Math.round(p.x)},${Math.round(p.y)}`)
        .join(';')
    : ''

  const box = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 800, h: 600 })
  const [view, setView] = useState<View>({ k: 1, x: 400, y: 300 })
  const [pos, setPos] = useState<Record<string, { x: number; y: number }>>({})
  const [hover, setHover] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const sim = useRef<Simulation<SimNode, SimLink> | null>(null)
  const simNodes = useRef(new Map<string, SimNode>())
  const fitted = useRef(false)
  const [settled, setSettled] = useState(0)
  // nodes whose phase just changed (or that just arrived) ring once, so a glance at the graph sees movement
  const [flash, setFlash] = useState<Record<string, number>>({})
  const seen = useRef<Map<string, string> | null>(null)
  // the first moments are the page filling in, not the factory moving
  const armed = useRef(false)
  const flashClear = useRef(0)
  useEffect(() => {
    const t = window.setTimeout(() => {
      armed.current = true
    }, 3_000)
    return () => window.clearTimeout(t)
  }, [])
  useEffect(() => {
    const before = seen.current
    const nowSeen = new Map(graph.nodes.map((n) => [n.id, `${n.phase ?? ''}`]))
    seen.current = nowSeen
    if (!before || !armed.current) return
    const changed = graph.nodes.filter((n) => before.get(n.id) !== nowSeen.get(n.id)).map((n) => n.id)
    if (!changed.length) return
    const at = Date.now()
    // out of the effect's own turn: the ring is drawn, then cleared when its animation is over
    const frame = requestAnimationFrame(() =>
      setFlash((f) => ({ ...f, ...Object.fromEntries(changed.map((id) => [id, at])) })),
    )
    // the clean-up is not cancelled by the next change, or stale rings replay on a remount
    flashClear.current = window.setTimeout(
      () =>
        setFlash((f) =>
          Object.fromEntries(Object.entries(f).filter(([, when]) => Date.now() - when < 1_800)),
        ),
      2_000,
    )
    return () => cancelAnimationFrame(frame)
  }, [graph])
  useEffect(() => () => window.clearTimeout(flashClear.current), [])

  // the box's size, and the view centred on it the first time
  useEffect(() => {
    const el = box.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => {
      if (!e) return
      const w = e.contentRect.width
      const h = e.contentRect.height
      setSize((s) => {
        if (s.w === w && s.h === h) return s
        setView((v) => ({ ...v, x: v.x + (w - s.w) / 2, y: v.y + (h - s.h) / 2 }))
        return { w, h }
      })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // what the effects below read without re-running on every frame
  const posRef = useRef(pos)
  const sizeRef = useRef(size)
  const modeRef = useRef(mode)
  const viewRef = useRef(view)
  const phoneRef = useRef(phone)
  useEffect(() => {
    posRef.current = pos
    sizeRef.current = size
    modeRef.current = mode
    viewRef.current = view
    phoneRef.current = phone
  })
  const anim = useRef(0)

  const fit = () => {
    // labels hang below and beside their nodes: more room across than down
    const v = frameFor(shown, mode, pos, size, phone)
    if (v) setView(v)
  }

  // one simulation for the page's life; ticks are written to state a frame at a time
  useEffect(() => {
    let frame = 0
    const s = forceSimulation<SimNode, SimLink>([])
      .force(
        'link',
        forceLink<SimNode, SimLink>([])
          .id((d) => d.id)
          .distance((l) => DISTANCE[l.kind])
          .strength((l) =>
            l.kind === 'looks-after' ? 0.25 : l.kind === 'builds' ? 0.12 : l.kind === 'deploys' ? 0.45 : 0.7,
          ),
      )
      .force(
        'charge',
        forceManyBody<SimNode>().strength((d) =>
          d.kind === 'prime' ? -600 : SMALL.has(d.kind) ? -150 : -220,
        ),
      )
      .force(
        'collide',
        forceCollide<SimNode>((d) => d.r * (d.kind === 'env' ? 1.4 : 1) + (SMALL.has(d.kind) ? 8 : 16)),
      )
      // a phone's glass is taller than wide: the pipeline's lane runs across the top there, not down the side
      .force(
        'x',
        forceX<SimNode>((d) => (phoneRef.current ? laneY(d) : laneX(d))).strength((d) =>
          PIPELINE.has(d.kind) ? 0.2 : 0.04,
        ),
      )
      .force(
        'y',
        forceY<SimNode>((d) => (phoneRef.current ? laneX(d) : laneY(d))).strength((d) =>
          PIPELINE.has(d.kind) ? 0.2 : 0.04,
        ),
      )
      .on('tick', () => {
        if (frame) return
        frame = requestAnimationFrame(() => {
          frame = 0
          const next: Record<string, { x: number; y: number }> = {}
          for (const n of s.nodes()) next[n.id] = { x: n.x ?? 0, y: n.y ?? 0 }
          setPos(next)
          // mostly cooled: good enough to frame (the full cool-down takes seconds)
          if (s.nodes().length > 1 && s.alpha() < 0.1) setSettled((n) => n || 1)
        })
      })
    sim.current = s
    return () => {
      s.stop()
      cancelAnimationFrame(frame)
    }
  }, [])

  // the graph changed: keep where known nodes are, place new ones beside what they link to
  // (only when its shape changed: a new phase or a poll's fresh copy moves nothing)
  const shapeKey = useRef('')
  useEffect(() => {
    const s = sim.current
    if (!s) return
    const key = `${graph.nodes.map((n) => n.id).join(',')}|${graph.links.map((l) => `${l.source}>${l.target}`).join(',')}`
    if (key === shapeKey.current) return
    shapeKey.current = key
    const known = simNodes.current
    const next = new Map<string, SimNode>()
    for (const n of graph.nodes) {
      const had = known.get(n.id)
      const near = graph.links.find((l) => l.target === n.id || l.source === n.id)
      const anchor = near ? known.get(near.source === n.id ? near.target : near.source) : undefined
      next.set(n.id, {
        ...n,
        r: RADIUS[n.kind],
        x: had?.x ?? (anchor?.x ?? 0) + (Math.random() - 0.5) * 60,
        y: had?.y ?? (anchor?.y ?? 0) + (Math.random() - 0.5) * 60,
        vx: had?.vx,
        vy: had?.vy,
        fx: n.kind === 'prime' ? 0 : had?.fx,
        fy: n.kind === 'prime' ? 0 : had?.fy,
      })
    }
    simNodes.current = next
    s.nodes([...next.values()])
    const link = s.force('link') as ReturnType<typeof forceLink<SimNode, SimLink>>
    link.links(graph.links.map((l) => ({ ...l })))
    // in a laid-out mode the nodes are placed, not simulated
    if (modeRef.current === 'everything') s.alpha(known.size ? 0.35 : 1).restart()
    else s.stop()
  }, [graph])

  // a mode (or its layout) changed: glide to the laid-out places, or back to floating
  useEffect(() => {
    const s = sim.current
    cancelAnimationFrame(anim.current)
    if (!s) return
    if (!targets) {
      // floating again: from where the nodes are now
      for (const n of simNodes.current.values()) {
        const p = posRef.current[n.id]
        if (p) {
          n.x = p.x
          n.y = p.y
          n.vx = 0
          n.vy = 0
        }
      }
      s.alpha(0.6).restart()
      return
    }
    s.stop()
    const from = posRef.current
    const startView = viewRef.current
    const endView = frameFor(shown, mode, targets, sizeRef.current, phoneRef.current)
    const ms = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 480
    const t0 = performance.now()
    fitted.current = true
    const step = (t: number) => {
      const f = ms ? Math.min(1, (t - t0) / ms) : 1
      const e = 1 - Math.pow(1 - f, 3)
      const next: Record<string, Pt> = {}
      for (const [id, to] of Object.entries(targets)) {
        const a = from[id] ?? to
        next[id] = { x: a.x + (to.x - a.x) * e, y: a.y + (to.y - a.y) * e }
      }
      setPos(next)
      if (endView)
        setView({
          k: startView.k + (endView.k - startView.k) * e,
          x: startView.x + (endView.x - startView.x) * e,
          y: startView.y + (endView.y - startView.y) * e,
        })
      if (f < 1) anim.current = requestAnimationFrame(step)
    }
    anim.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(anim.current)
    // the targets' key, not their identity: a poll that moves nothing glides nothing
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey, mode])

  // fit once the first layout has settled (the simulation's end)
  useEffect(() => {
    if (!settled || fitted.current || Object.keys(pos).length < 2) return
    fitted.current = true
    fit()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settled])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setSelected(null)
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // ---- pointer: pan the background, drag a node, click selects; two fingers pinch ----
  const drag = useRef<{
    id?: string
    sx: number
    sy: number
    vx: number
    vy: number
    moved: boolean
    slop: number
  } | null>(null)
  const fingers = useRef(new Map<number, Pt>())
  const pinch = useRef<Touch2 | null>(null)
  const inBox = (cx: number, cy: number) => {
    const r = box.current?.getBoundingClientRect()
    return { x: cx - (r?.left ?? 0), y: cy - (r?.top ?? 0) }
  }
  const toGraph = (cx: number, cy: number) => {
    const p = inBox(cx, cy)
    return { x: (p.x - view.x) / view.k, y: (p.y - view.y) / view.k }
  }
  const releaseNode = (id?: string) => {
    if (!id) return
    const n = simNodes.current.get(id)
    if (n && n.kind !== 'prime') {
      n.fx = null
      n.fy = null
    }
    sim.current?.alphaTarget(0)
  }
  const down = (e: ReactPointerEvent, id?: string) => {
    e.stopPropagation()
    ;(e.currentTarget as Element).setPointerCapture(e.pointerId)
    fingers.current.set(e.pointerId, inBox(e.clientX, e.clientY))
    if (fingers.current.size === 2) {
      // a second finger: whatever the first was doing becomes a pinch
      releaseNode(drag.current?.id)
      drag.current = null
      const [a, b] = [...fingers.current.values()] as [Pt, Pt]
      const v = viewRef.current
      pinch.current = {
        d: Math.hypot(a.x - b.x, a.y - b.y),
        mx: (a.x + b.x) / 2,
        my: (a.y + b.y) / 2,
        view: v,
      }
      return
    }
    if (fingers.current.size > 2) return
    drag.current = {
      id,
      sx: e.clientX,
      sy: e.clientY,
      vx: view.x,
      vy: view.y,
      moved: false,
      // a fingertip wobbles more than a mouse
      slop: e.pointerType === 'mouse' ? 4 : 10,
    }
  }
  const move = (e: ReactPointerEvent) => {
    if (fingers.current.has(e.pointerId)) fingers.current.set(e.pointerId, inBox(e.clientX, e.clientY))
    if (pinch.current) {
      if (fingers.current.size >= 2) {
        const [a, b] = [...fingers.current.values()] as [Pt, Pt]
        setView(pinchView(pinch.current, a, b))
      }
      return
    }
    const d = drag.current
    if (!d) return
    const dx = e.clientX - d.sx
    const dy = e.clientY - d.sy
    if (!d.moved && Math.hypot(dx, dy) < d.slop) return
    d.moved = true
    if (!d.id) {
      setView((v) => ({ ...v, x: d.vx + dx, y: d.vy + dy }))
      return
    }
    const n = simNodes.current.get(d.id)
    // a laid-out mode places its nodes: they are not moved by hand
    if (!n || n.kind === 'prime' || modeRef.current !== 'everything') return
    const p = toGraph(e.clientX, e.clientY)
    n.fx = p.x
    n.fy = p.y
    sim.current?.alphaTarget(0.25).restart()
  }
  const up = (e: ReactPointerEvent) => {
    fingers.current.delete(e.pointerId)
    if (pinch.current) {
      // the pinch ends with the first finger up; the other one waits for its own lift
      if (fingers.current.size < 2) pinch.current = null
      return
    }
    const d = drag.current
    drag.current = null
    if (!d) return
    releaseNode(d.id)
    if (!d.moved) setSelected(d.id ?? null)
  }
  const wheel = (e: React.WheelEvent) => {
    const r = box.current?.getBoundingClientRect()
    const mx = e.clientX - (r?.left ?? 0)
    const my = e.clientY - (r?.top ?? 0)
    setView((v) => {
      const k = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v.k * Math.exp(-e.deltaY * 0.0015)))
      return { k, x: mx - ((mx - v.x) * k) / v.k, y: my - ((my - v.y) * k) / v.k }
    })
  }

  // a finger has no hover: on a phone the chosen node keeps its neighbours lit
  const litId = hover ?? (phone ? selected : null)
  const lit = litId ? neighbours(shown, litId) : null
  const at = (id: string) => pos[id]
  // on a phone the glass is small and the graph is zoomed out: nodes and words are drawn no
  // smaller than a fingertip and a legible line, whatever the zoom (desktop draws them as is)
  const rOf = (n: GraphNode) => (phone ? screenSize(RADIUS[n.kind], view.k, 8, 2) : RADIUS[n.kind])
  const fsOf = (n: GraphNode) => (phone ? screenSize(labelSize(n), view.k, 11, 2.5) : labelSize(n))
  /** below the node: its radius and a line, in step with the label's size */
  const labelDy = (n: GraphNode) => rOf(n) + 14 * (fsOf(n) / labelSize(n))
  const chosen = selected ? shown.nodes.find((n) => n.id === selected) : undefined
  // the key names only what is drawn
  const inView = useMemo(() => new Set(shown.nodes.map((n) => n.kind)), [shown])
  const [keyOpen, setKeyOpen] = useState(false)
  const statusOf = useRunStatus()
  const holderOf = (n: GraphNode) =>
    n.kind === 'agent' && n.run ? statusOf(n.run, n.phase).holder : undefined
  // something waits on the owner: the waiting button carries a lit dot (never a count)
  const onYou = useMemo(
    () => factory && waitingOf({ runs: runs ?? [], cards, builds, now }).some((w) => w.holder === 'you'),
    [factory, runs, cards, builds, now],
  )
  // a waiting agent's row chooses its node when the view shows it, else its link goes
  const pick = (w: Waiting) => {
    const id = 'run' in w.to ? w.to.run : ''
    if (!id || !shown.nodes.some((n) => n.id === id)) return false
    setWaitingSheet(false)
    setSelected(id)
    return true
  }
  // Approvals is a list: a card's title reads beside it, its ✓ and × before it
  const listed = mode === 'approvals'
  const beside = (n: GraphNode) => listed && n.kind === 'card'
  // the labels to draw: small kinds only when zoomed in, lit or chosen, and none over another
  const labelled = declutterLabels(
    shown.nodes.flatMap((n) => {
      const p = pos[n.id]
      if (!p) return []
      const forced = !!lit?.has(n.id) || n.id === selected
      // a laid-out mode has room: its small things are named, the collision check keeps them apart
      if (SMALL.has(n.kind) && view.k < 1.5 && !forced && mode === 'everything') return []
      const fs = fsOf(n)
      const text = labelText(n, listed, phone)
      const w = text.length * fs * 0.56
      return [
        {
          id: n.id,
          x: beside(n) ? p.x + rOf(n) + 10 + w / 2 : p.x,
          y: beside(n) ? p.y : p.y + labelDy(n) - fs * 0.35,
          w,
          h: fs + 2,
          priority: forced ? 100 : LABEL_PRIORITY[n.kind] + (holderOf(n) === 'you' ? 5 : 0),
          always: n.kind === 'agent',
        },
      ]
    }),
    shown.nodes.flatMap((n) => {
      const p = pos[n.id]
      return p ? [{ id: n.id, x: p.x, y: p.y, r: rOf(n) }] : []
    }),
  )
  const showLabel = (n: GraphNode) => labelled.has(n.id)

  return (
    <div
      className={cx(
        'flex flex-col min-h-[420px]',
        // a phone: the screen less the top inset, the page's padding and the room the tab bar keeps
        'h-[calc(100dvh-env(safe-area-inset-top)-6.75rem)] md:h-[calc(100dvh-5rem)] lg:h-[calc(100dvh-3rem)]',
      )}
      data-testid="agent-graph"
    >
      <PageTitle
        right={
          phone ? (
            factory ? (
              <Link
                to="/agents/pipeline"
                aria-label="Pipeline"
                title="Pipeline"
                className="inline-flex items-center justify-center min-h-11 min-w-11 text-muted hover:text-fg"
                data-testid="graph-pipeline"
              >
                <GitMerge size={18} aria-hidden />
              </Link>
            ) : undefined
          ) : (
            <span className="flex items-center justify-end gap-x-3">
              <span
                className="flex items-center rounded-full border border-line p-0.5"
                role="group"
                aria-label="View"
                data-testid="graph-modes"
              >
                {MODES.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    title={m.blurb}
                    aria-pressed={mode === m.key}
                    onClick={() => setMode(m.key)}
                    className={cx(
                      'rounded-full px-2.5 min-h-7 text-[12px] whitespace-nowrap transition-colors',
                      mode === m.key ? 'bg-raised text-fg font-semibold' : 'text-muted hover:text-fg',
                    )}
                    data-testid={`graph-mode-${m.key}`}
                  >
                    {m.label}
                  </button>
                ))}
              </span>
              <label className="flex items-center gap-1.5 text-[13px] text-muted cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={week}
                  onChange={(e) => setWeekSaved(e.target.checked)}
                  data-testid="graph-week"
                />
                Ended this week
              </label>
              <IconButton label="Fit" onClick={fit}>
                <Maximize2 size={16} aria-hidden />
              </IconButton>
              {factory && (
                <button
                  type="button"
                  aria-pressed={docked && !chosen}
                  onClick={() => {
                    if (chosen) {
                      setSelected(null)
                      setDocked(true)
                    } else setDocked(!docked)
                  }}
                  className={cx(
                    'relative inline-flex items-center gap-1.5 rounded-full border px-3 min-h-8 text-[13px] transition-colors',
                    docked && !chosen ? 'border-line-strong text-fg' : 'border-line text-muted hover:text-fg',
                  )}
                  data-testid="graph-waiting-toggle"
                >
                  <Hourglass size={14} aria-hidden />
                  Waiting
                  {onYou && <WaitingDot inline />}
                </button>
              )}
              {factory && (
                <Link
                  to="/agents/pipeline"
                  className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 min-h-8 text-[13px] text-muted hover:text-fg"
                  data-testid="graph-pipeline"
                >
                  <GitMerge size={14} aria-hidden />
                  Pipeline
                </Link>
              )}
            </span>
          )
        }
      >
        Graph
      </PageTitle>
      {!phone && (
        <div className="flex items-center gap-1 mb-2 h-8 overflow-x-auto" role="group" aria-label="Layers">
          {LAYERS.filter((l) => layerInMode(l.key, mode)).map((l) => {
            const needed = layerRequired(l.key, mode)
            const isOn = on[l.key]
            return (
              <button
                key={l.key}
                type="button"
                aria-pressed={isOn}
                disabled={needed}
                title={needed ? 'Part of this view' : undefined}
                onClick={() => toggleLayer(l.key)}
                className={cx(
                  'rounded-full border px-2.5 min-h-7 text-[12px] whitespace-nowrap transition-colors',
                  isOn ? 'border-line-strong text-fg' : 'border-line text-faint hover:text-muted',
                  needed && 'opacity-70 cursor-default',
                )}
                data-testid={`graph-layer-${l.key}`}
              >
                {l.label}
              </button>
            )
          })}
          <button
            type="button"
            aria-pressed={keyOpen}
            onClick={() => setKeyOpen((v) => !v)}
            className={cx(
              'ml-auto rounded-full border px-2.5 min-h-7 text-[12px] whitespace-nowrap transition-colors',
              keyOpen ? 'border-line-strong text-fg' : 'border-line text-faint hover:text-muted',
            )}
            data-testid="graph-key"
          >
            Key
          </button>
        </div>
      )}

      <div className="relative flex-1 min-h-0 flex gap-3">
        <div
          ref={box}
          className="relative flex-1 min-w-0 rounded-[var(--radius-card)] border border-line bg-panel/40 overflow-hidden touch-none select-none"
          onPointerDown={(e) => down(e)}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          onWheel={wheel}
        >
          {!phone && keyOpen && (
            <div
              className="absolute left-2 bottom-2 z-10 max-w-[min(32rem,calc(100%-1rem))] rounded-[var(--radius-card-inner)] border border-line bg-panel px-3 py-2 fade-in"
              onPointerDown={(e) => e.stopPropagation()}
            >
              <Legend kinds={inView} />
            </div>
          )}
          {shown.nodes.length === 0 ? (
            <p className="absolute inset-0 flex items-center justify-center text-sm text-muted px-6 text-center">
              {mode === 'everything' || mode === 'work'
                ? 'No agents right now. Prime and the agents it looks after show here as they start.'
                : MODES.find((m) => m.key === mode)?.empty}
            </p>
          ) : (
            <svg width={size.w} height={size.h} className="absolute inset-0 block" aria-label="Graph">
              <g transform={`translate(${view.x},${view.y}) scale(${view.k})`}>
                {headingsFor(shown, mode, pos).map((h) => (
                  <text
                    key={h.label}
                    x={h.x}
                    y={h.y}
                    textAnchor={h.anchor ?? 'middle'}
                    fontSize={phone ? screenSize(11, view.k, 11, 2.5) : 11}
                    fill="var(--color-faint)"
                    className="graph-label"
                    data-testid="graph-heading"
                  >
                    {h.label}
                  </text>
                ))}
                {shown.links.map((l) => {
                  const a = at(l.source)
                  const b = at(l.target)
                  if (!a || !b) return null
                  const on = !lit || (lit.has(l.source) && lit.has(l.target))
                  return (
                    <line
                      key={`${l.source}-${l.target}`}
                      x1={a.x}
                      y1={a.y}
                      x2={b.x}
                      y2={b.y}
                      stroke={
                        l.kind === 'asks'
                          ? graphKindColour('card')
                          : l.kind === 'works-on' || l.kind === 'deploys'
                            ? 'var(--color-line-strong)'
                            : 'var(--color-line)'
                      }
                      strokeWidth={l.kind === 'works-on' || l.kind === 'deploys' ? 1.6 : 1.1}
                      strokeOpacity={l.kind === 'asks' ? 0.7 : 1}
                      strokeDasharray={
                        l.kind === 'looks-after'
                          ? '3 5'
                          : l.kind === 'uses' || l.kind === 'tracks'
                            ? '1.5 3'
                            : l.kind === 'deploys'
                              ? '6 3'
                              : undefined
                      }
                      opacity={on ? 1 : 0.15}
                      className="transition-opacity"
                    />
                  )
                })}
                {shown.nodes.map((n) => {
                  const p = at(n.id)
                  if (!p) return null
                  const r = rOf(n)
                  const holder = holderOf(n)
                  const colour = nodeColour(n, holder)
                  const dim = !!lit && !lit.has(n.id)
                  const pr = n.run?.spec.pr_number
                  return (
                    <g
                      key={n.id}
                      transform={`translate(${p.x},${p.y})`}
                      opacity={dim ? 0.2 : 1}
                      className="transition-opacity cursor-pointer outline-none"
                      role="button"
                      tabIndex={0}
                      aria-label={`${n.label}${n.kind === 'agent' && n.run ? `, ${statusOf(n.run, n.phase).word}` : ''}`}
                      onPointerDown={(e) => down(e, n.id)}
                      onPointerEnter={() => setHover(n.id)}
                      onPointerLeave={() => setHover((h) => (h === n.id ? null : h))}
                      onKeyDown={(e) => e.key === 'Enter' && setSelected(n.id)}
                      data-testid={`graph-node-${n.kind}`}
                    >
                      {phone && (
                        // a fingertip's worth of target round a small node
                        <circle r={Math.max(r, 22 / view.k)} fill="transparent" data-testid="graph-hit" />
                      )}
                      {((n.kind === 'agent' && (n.phase === 'Working' || n.phase === 'Starting')) ||
                        (n.kind === 'build' && n.phase === 'building') ||
                        (n.kind === 'ship' && n.phase !== 'failed')) && (
                        <circle r={r} fill="none" stroke={colour} strokeWidth={2} className="graph-pulse" />
                      )}
                      {flash[n.id] && (
                        <circle
                          key={flash[n.id]}
                          r={r}
                          fill="none"
                          stroke={colour}
                          strokeWidth={2.5}
                          className="graph-flash"
                          data-testid="graph-flash"
                        />
                      )}
                      <Shape n={n} r={r} colour={colour} />
                      {((n.kind === 'agent' && holder === 'you') || n.kind === 'card') && (
                        <circle
                          r={r + 4}
                          fill="none"
                          stroke={colour}
                          strokeWidth={1.5}
                          strokeDasharray="2 3"
                        />
                      )}
                      {n.id === selected && (
                        <circle r={r + 7} fill="none" stroke="var(--color-fg)" strokeWidth={1} />
                      )}
                      {pr ? (
                        <text
                          x={r + 3}
                          y={-r + 2}
                          fontSize={9.5}
                          fill="var(--color-muted)"
                          className="font-mono"
                        >
                          #{pr}
                        </text>
                      ) : null}
                      {showLabel(n) && (
                        <text
                          x={beside(n) ? r + 10 : undefined}
                          y={beside(n) ? fsOf(n) * 0.35 : labelDy(n)}
                          textAnchor={beside(n) ? 'start' : 'middle'}
                          fontSize={fsOf(n)}
                          fill={
                            n.kind === 'agent' || n.kind === 'prime' || n.kind === 'card'
                              ? 'var(--color-fg)'
                              : 'var(--color-muted)'
                          }
                          className="graph-label"
                        >
                          {labelText(n, listed, phone)}
                        </text>
                      )}
                    </g>
                  )
                })}
                {shown.nodes.map((n) => {
                  const p = at(n.id)
                  // on a touch screen a pair a card would swamp the graph: only the one chosen or lit
                  const shows = !phone || listed || n.id === selected || !!lit?.has(n.id)
                  return n.card && p && shows ? (
                    <CardButtons
                      key={n.id}
                      s={n.card}
                      x={p.x}
                      y={p.y}
                      from={rOf(n)}
                      k={view.k}
                      touch={phone}
                      side={listed ? 'left' : 'right'}
                    />
                  ) : null
                })}
              </g>
            </svg>
          )}
        </div>
        {chosen && !phone ? (
          <NodePanel
            node={chosen}
            near={whole.nodes.filter((n) => n.id !== chosen.id && neighbours(whole, chosen.id).has(n.id))}
            builds={builds}
            onSelect={setSelected}
            onClose={() => setSelected(null)}
          />
        ) : (
          !phone &&
          waitingShown && <WaitingDock builds={builds} onPick={pick} onClose={() => setDocked(false)} />
        )}
      </div>
      {phone && (
        <>
          <GraphPhoneBar
            modes={MODES}
            mode={mode}
            onMode={(m) => setMode(m as GraphMode)}
            onFit={fit}
            onLayers={() => setLayersOpen(true)}
            onWaiting={factory ? () => setWaitingSheet(true) : undefined}
            onYou={onYou}
          />
          <Sheet open={waitingSheet} onClose={() => setWaitingSheet(false)} title="What is waiting">
            <WaitingBoard
              builds={builds}
              onGo={() => setWaitingSheet(false)}
              onPick={pick}
              empty="Nothing is in flight."
            />
          </Sheet>
          <GraphLayersSheet
            open={layersOpen}
            onClose={() => setLayersOpen(false)}
            layers={LAYERS.filter((l) => layerInMode(l.key, mode)).map((l) => ({
              key: l.key,
              label: l.label,
              on: on[l.key],
              needed: layerRequired(l.key, mode),
            }))}
            onToggle={(k) => toggleLayer(k as Layer)}
            week={week}
            onWeek={setWeekSaved}
          >
            <Legend kinds={inView} />
          </GraphLayersSheet>
          <Sheet
            open={!!chosen}
            onClose={() => setSelected(null)}
            title={chosen ? (chosen.kind === 'card' ? 'Prime is waiting for you' : chosen.label) : undefined}
          >
            {chosen && (
              <NodePanel
                sheet
                node={chosen}
                near={whole.nodes.filter((n) => n.id !== chosen.id && neighbours(whole, chosen.id).has(n.id))}
                builds={builds}
                onSelect={setSelected}
                onClose={() => setSelected(null)}
              />
            )}
          </Sheet>
        </>
      )}
    </div>
  )
}

/**
 * Approve and put away, beside a waiting card's node: the card's own answers
 * (its first button and ×, through the thread's `answerAsk`/`acceptCard`/
 * dismiss and the same tap guard), so the graph can be worked from without
 * opening the panel. The pointer never reaches the node or the pan beneath.
 * Drawn in screen pixels whatever the zoom (the group is scaled back by it): a
 * fingertip's 44 on a touch screen, a small pair on a desk.
 */
const CardButtons = ({
  s,
  x,
  y,
  from,
  k,
  touch,
  side = 'right',
}: {
  s: SuggestionResource
  x: number
  y: number
  /** how far from the node's centre the first button sits, in graph units */
  from: number
  k: number
  touch: boolean
  /** which side of the node: right in the free graph, left in Approvals' list (titles to the right) */
  side?: 'left' | 'right'
}) => {
  const tap = useCardTap()
  const [busy, setBusy] = useState(false)
  const options = s.spec.options ?? []
  const ask = s.spec.type === 'ask'
  const yes = ask ? (options[0] ?? 'Yes') : 'Do it'
  const lastIndex = options.length - 1
  const run = (fn: () => Promise<unknown>) =>
    void tap(async () => {
      setBusy(true)
      try {
        await fn()
      } finally {
        setBusy(false)
      }
    })
  const approve = () => run(() => (ask ? answerAsk(s, yes, 0) : acceptCard(s)))
  const away = () =>
    run(() => (ask && lastIndex > 0 ? answerAsk(s, options[lastIndex]!, lastIndex) : dismissSuggestion(s)))
  // screen pixels: the drawn circle, the target round it, and the step between the two
  const radius = touch ? 17 : 12
  const hit = touch ? 22 : 13
  const step = touch ? 46 : 28
  const icon = touch ? 16 : 12
  const button = (
    cx: number,
    label: string,
    testid: string,
    onClick: () => void,
    children: React.ReactNode,
  ) => (
    <g
      transform={`translate(${cx},0)`}
      role="button"
      tabIndex={0}
      aria-label={label}
      aria-disabled={busy}
      className="cursor-pointer outline-none graph-act"
      opacity={busy ? 0.4 : 1}
      onPointerDown={(e) => e.stopPropagation()}
      onPointerUp={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
      data-testid={testid}
    >
      <title>{label}</title>
      <circle r={hit} fill="transparent" />
      <circle r={radius} fill="var(--color-panel)" stroke="var(--color-line-strong)" strokeWidth={1} />
      {children}
    </g>
  )
  // the first button's centre, in screen pixels from the node's
  const first = from * k + 6 + radius
  const at = (i: number) => (side === 'right' ? first + i * step : -(first + i * step))
  return (
    <g transform={`translate(${x},${y}) scale(${1 / k})`} data-card-id={s.id}>
      {button(
        at(side === 'right' ? 0 : 1),
        `${yes}: ${s.spec.title}`,
        'graph-approve',
        approve,
        <Check
          x={-icon / 2}
          y={-icon / 2}
          size={icon}
          stroke="var(--color-accent)"
          strokeWidth={2.5}
          aria-hidden
        />,
      )}
      {button(
        at(side === 'right' ? 1 : 0),
        `${ask && lastIndex > 0 ? options[lastIndex] : 'Dismiss'}: ${s.spec.title}`,
        'graph-dismiss',
        away,
        <X
          x={-icon / 2.4}
          y={-icon / 2.4}
          size={icon / 1.2}
          stroke="var(--color-muted)"
          strokeWidth={2.5}
          aria-hidden
        />,
      )}
    </g>
  )
}

/** A shape for the legend, drawn as the graph draws it. */
const Glyph = ({ kind, phase, colour }: { kind: GraphNode['kind']; phase?: string; colour: string }) => (
  <svg width={18} height={16} viewBox="-9 -8 18 16" className="shrink-0" aria-hidden>
    <Shape n={{ kind, phase }} r={kind === 'env' ? 5 : 6} colour={colour} />
  </svg>
)

/** The agents' colours in words: who holds the work, as every chip says it. */
const AGENT_KEY = [
  ['Working', 'Working'],
  ['Waiting', 'Your turn'],
  ['Suspended', 'With Prime, or idle'],
  ['Pending', 'Waiting for a sandbox'],
  ['Failed', 'Stopped'],
] as const

/** The key to what is drawn: only the kinds in view (and the agents' states when there are agents). */
const Legend = ({ kinds }: { kinds: Set<GraphNode['kind']> }) => (
  <div
    className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted"
    data-testid="graph-legend"
  >
    {(kinds.has('agent') ? AGENT_KEY : []).map(([phase, words]) => (
      <span key={phase} className="flex items-center gap-1.5">
        <span className="h-2.5 w-2.5 rounded-full" style={{ background: graphPhaseColour(phase) }} />
        {words}
      </span>
    ))}
    {(
      [
        ['task', 'Task', 'var(--color-fg-2)'],
        ['project', 'Project', 'var(--color-muted)'],
        ['card', 'Waiting for you', graphKindColour('card')],
        ['issue', 'Issue', graphKindColour('issue')],
        ['doc', 'Page in use', graphKindColour('doc')],
        ['build', 'Build', graphKindColour('build', 'building')],
        ['ship', 'Ship to prod', graphKindColour('ship')],
        ['env', 'Prod and staging', graphKindColour('env')],
        ['node', 'Cluster node', graphKindColour('node', 'ready')],
        ['sandbox', 'Sandbox', graphKindColour('sandbox', 'ready')],
        ['problem', 'Pod to look at', graphKindColour('problem')],
        ['app', 'Argo app', graphKindColour('app', 'Healthy')],
        ['spend', 'Spend today', graphKindColour('spend')],
      ] as const
    )
      .filter(([kind]) => kinds.has(kind))
      .map(([kind, words, colour]) => (
        <span key={kind} className="flex items-center gap-1" data-testid={`graph-legend-${kind}`}>
          <Glyph kind={kind} phase={kind === 'build' ? 'building' : undefined} colour={colour} />
          {words}
        </span>
      ))}
  </div>
)

const linkClass = 'text-sm text-accent-text underline max-md:inline-flex max-md:items-center max-md:min-h-11'

/** What a node is, beside the graph (below it on a narrow window). */
const NodePanel = ({
  node,
  near,
  builds,
  onSelect,
  onClose,
  sheet = false,
}: {
  node: GraphNode
  /** the nodes it is linked to */
  near: GraphNode[]
  builds: Builds | null
  onSelect: (id: string) => void
  onClose: () => void
  /** in a bottom sheet (a phone): the sheet has the title, the close and the scrolling */
  sheet?: boolean
}) => (
  <aside
    className={cx(
      'flex flex-col gap-2',
      sheet
        ? 'pb-2'
        : 'absolute inset-x-0 bottom-0 max-h-[60%] md:static md:max-h-none md:w-80 shrink-0 card bg-panel p-3 overflow-y-auto fade-in',
    )}
    data-testid="graph-panel"
    data-kind={node.kind}
  >
    {!sheet && (
      <div className="flex items-start gap-2">
        <h2 className="flex-1 font-semibold leading-snug">
          {node.kind === 'card' ? 'Prime is waiting for you' : node.label}
        </h2>
        <IconButton label="Close" onClick={onClose}>
          <X size={16} aria-hidden />
        </IconButton>
      </div>
    )}
    {node.run ? (
      <RunDetail run={node.run} phase={node.phase ?? node.run.spec.status} lead={node.kind === 'prime'} />
    ) : node.task ? (
      <>
        <Tag>{node.task.status.state}</Tag>
        <Link to="/tasks/$id" params={{ id: node.task.id }} className={linkClass}>
          Open the task
        </Link>
      </>
    ) : node.project ? (
      <Link to="/projects/$id" params={{ id: node.project.id }} className={linkClass}>
        Open the project
      </Link>
    ) : node.card ? (
      <CardDetail node={node} />
    ) : node.issue ? (
      <IssueDetail issue={node.issue} />
    ) : node.page ? (
      <>
        {node.page.spec.text && (
          <p className="text-[13px] text-muted leading-snug line-clamp-5 whitespace-pre-wrap break-words">
            {node.page.spec.text.slice(0, 400)}
          </p>
        )}
        <p className="text-[12px] text-faint">Changed {fmtAgo(node.page.updated_at)}</p>
        <Link to="/pages/$id" params={{ id: node.page.id }} className={linkClass}>
          Open the page
        </Link>
      </>
    ) : node.build ? (
      <>
        <div className="flex flex-wrap items-center gap-1.5">
          <Tag tone={node.build.state === 'building' ? 'accent' : undefined}>
            {node.build.state === 'building' ? 'Building' : 'Queued'}
          </Tag>
          <span className="font-mono text-[11px] text-faint">{node.build.sha.slice(0, 12)}</span>
        </div>
        {node.build.note && <p className="text-[13px] leading-snug">{node.build.note}</p>}
        {node.build.started && <p className="text-[12px] text-faint">Started {fmtAgo(node.build.started)}</p>}
        <Link
          to="/dev/releases"
          search={{ build: node.build.name } as never}
          className={linkClass}
          data-testid="graph-build-log"
        >
          Its log on Releases
        </Link>
      </>
    ) : node.ship && builds ? (
      <>
        <ShipPipeline builds={builds} unreachable={false} />
        <Link to="/dev/releases" className={linkClass}>
          Open Releases
        </Link>
      </>
    ) : node.host ? (
      <HostDetail node={node.host} />
    ) : node.sandbox ? (
      <>
        <div className="flex flex-wrap items-center gap-1.5">
          <Tag tone={node.phase === 'ready' ? undefined : 'warn'}>
            {node.phase === 'ready' ? 'Running' : node.phase}
          </Tag>
          <Tag>{node.sandbox.template}</Tag>
        </div>
        <p className="text-[13px] text-muted">
          {node.sandbox.node ? `On ${node.sandbox.node}` : 'Not placed on a node yet'}
          {node.sandbox.since ? `, up ${fmtAgo(node.sandbox.since).replace(/ ago$/, '')}` : ''}
        </p>
        {node.sandbox.reason && <p className="text-[13px] text-muted">{podWords(node.sandbox.reason)}</p>}
      </>
    ) : node.problem ? (
      <ProblemDetail pod={node.problem} />
    ) : node.app ? (
      <>
        <AppTags app={node.app} />
        <p className="text-[12px] text-faint">Argo CD application</p>
      </>
    ) : node.spend ? (
      <>
        <p className="text-[13px]">
          {node.spend.notional ? 'About ' : ''}${node.spend.usd.toFixed(2)} and {node.spend.turns}{' '}
          {node.spend.turns === 1 ? 'turn' : 'turns'} today, Prime and the agents together.
          {node.spend.notional ? ' A subscription pays, so this is what the API would have charged.' : ''}
        </p>
        <Link to="/agents/stats" className={linkClass}>
          Open the stats
        </Link>
      </>
    ) : node.env ? (
      <>
        <p className="text-[13px]">
          {node.env.name === 'prod' ? 'Prod' : 'Staging'} runs{' '}
          <span className="font-mono text-[12px]">{node.env.version.slice(0, 12)}</span>
        </p>
        <EnvNote version={node.env.version} builds={builds} />
        <Link to="/dev/releases" className={linkClass}>
          Open Releases
        </Link>
      </>
    ) : null}
    {near.length > 0 && node.kind !== 'prime' && (
      <div className="mt-1 pt-2 border-t border-line">
        <p className="label text-faint mb-1">Linked to</p>
        <ul className="flex flex-col">
          {near.slice(0, 8).map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => onSelect(n.id)}
                className={cx(
                  'flex w-full items-center gap-1.5 text-left text-[13px] text-fg-2 hover:text-fg',
                  sheet ? 'min-h-11' : 'min-h-8',
                )}
              >
                <Glyph kind={n.kind} phase={n.phase} colour={nodeColour(n)} />
                <span className="truncate">{n.label}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    )}
  </aside>
)

/** The commit subject of the build an environment runs, when the builds list it. */
const EnvNote = ({ version, builds }: { version: string; builds: Builds | null }) => {
  const note = builds?.builds.find((b) => runsVersion(version, b.sha))?.note
  return note ? <p className="text-[13px] text-muted leading-snug">{note}</p> : null
}

/**
 * A card Prime is waiting on, answered here exactly as in its thread: the same
 * card (its buttons, Do it / No thanks, the ×, the tap-settle guard). Once it is
 * answered it is no longer pending, so it leaves the graph and this panel closes.
 */
const CardDetail = ({ node }: { node: GraphNode }) => {
  const { titleOf } = useNames()
  if (!node.card) return null
  return (
    <>
      <div data-testid="graph-card">
        <Proposal s={node.card} titleOf={titleOf} acts={false} />
      </div>
      <Link to="/agents" className={linkClass}>
        Open Prime's thread
      </Link>
    </>
  )
}

const IssueDetail = ({ issue }: { issue: GitIssueResource }) => {
  const [open, setOpen] = useState(false)
  const s = issue.spec
  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        <Tag>
          {s.repo}#{s.number}
        </Tag>
        {s.labels.map((l) => (
          <Tag key={l}>{l}</Tag>
        ))}
        {s.assignees.length > 0 && <Tag>→ {s.assignees.join(', ')}</Tag>}
      </div>
      {s.body && (
        <p className="text-[13px] text-muted leading-snug line-clamp-5 whitespace-pre-wrap break-words">
          {s.body.slice(0, 400)}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={() => setOpen(true)} className={linkClass}>
          Open the issue
        </button>
        {s.source.url && (
          <a
            href={s.source.url}
            target="_blank"
            rel="noopener noreferrer"
            className={cx(linkClass, 'inline-flex items-center gap-1')}
          >
            On Forgejo <ExternalLink size={12} aria-hidden />
          </a>
        )}
      </div>
      {open && <MirrorSheet resource={issue} onClose={() => setOpen(false)} />}
    </>
  )
}

const RunDetail = ({ run, phase, lead }: { run: AgentRunResource; phase: string; lead: boolean }) => {
  const name = run.spec.name ?? ''
  const live = useStream((s) => s.runLines[name])
  const [text, setText] = useState('')
  const [error, setError] = useState('')
  const lines = useMemo(() => {
    const stored = run.spec.messages ?? []
    const last = stored.length ? stored[stored.length - 1]!.seq : 0
    return [...stored, ...(live ?? []).filter((l) => l.seq > last)]
  }, [run.spec.messages, live])
  // the app's note to Prime about an edited artifact is never shown as the owner's words
  // what the app or the factory said to Prime is not the owner's words, and its framing never shows
  const words = lines
    .filter((l) => l.role === 'assistant' || (l.role === 'user' && !appLineOf(l.text)))
    .map((l) => (l.role === 'user' ? { ...l, text: splitArtifactNote(l.text).words.trim() } : l))
    .filter((l) => l.text)
    .slice(-3)
  const lastTool = [...lines].reverse().find((l) => l.role === 'tool')
  const doing = phase === 'Working' && lastTool ? stepOf(lastTool.text) : undefined
  const send = async () => {
    const t = text.trim()
    if (!t) return
    setError('')
    try {
      await mutate.action(run.id, 'say', { text: t })
      setText('')
    } catch (e) {
      setError((e as Error).message)
    }
  }
  return (
    <>
      <div className="flex flex-wrap items-center gap-1.5">
        <RunTag run={run} phase={phase} />
        {run.spec.pr_number ? (
          <Tag>
            PR #{run.spec.pr_number} {run.spec.pr_state ?? 'open'}
          </Tag>
        ) : null}
        {!lead && run.spec.repo_url && (
          <span className="font-mono text-[11px] text-faint">{repoName(run.spec.repo_url)}</span>
        )}
      </div>
      {doing && (
        <p className="text-[12.5px] text-muted">
          Now: {doing.label}
          {doing.detail && (
            <span className="font-mono text-[11.5px] text-faint"> {doing.detail.slice(0, 80)}</span>
          )}
        </p>
      )}
      <ul className="flex flex-col gap-1.5">
        {words.map((l) => (
          <li
            key={l.seq}
            className={cx(
              'text-[13px] leading-snug whitespace-pre-wrap break-words line-clamp-6',
              l.role === 'user' ? 'text-muted' : 'text-fg',
            )}
          >
            {l.role === 'user' ? 'You: ' : ''}
            {l.text}
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap gap-3 text-sm">
        <Link
          to={lead ? '/agents' : '/agents/run/$id'}
          params={lead ? undefined : { id: run.id }}
          className="text-accent-text underline max-md:min-h-11 max-md:inline-flex max-md:items-center"
        >
          Open the thread
        </Link>
        {run.spec.pr_number ? (
          <Link
            to="/dev/review/$id"
            params={{ id: run.id }}
            className="text-accent-text underline max-md:min-h-11 max-md:inline-flex max-md:items-center"
          >
            Review the PR
          </Link>
        ) : null}
      </div>
      {!isOver(phase) && (
        <form
          className="flex items-center gap-1.5 mt-1"
          onSubmit={(e) => {
            e.preventDefault()
            void send()
          }}
        >
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={lead ? 'Tell Prime…' : 'Tell this agent…'}
            className="flex-1 min-w-0 rounded-full px-3 h-9 max-md:h-11 text-sm"
            data-testid="graph-say"
          />
          <IconButton label="Send" onClick={() => void send()}>
            <ArrowUp size={16} aria-hidden />
          </IconButton>
        </form>
      )}
      {error && <p className="text-xs text-rose-300">{error}</p>}
    </>
  )
}
